/**
 * TanStack Query hooks for the system_settings admin endpoints (Phase 13).
 *
 * Wraps:
 *   GET    /api/v1/admin/settings              -> useAdminSettings
 *   PUT    /api/v1/admin/settings/{key}        -> useUpsertSetting
 *   DELETE /api/v1/admin/settings/{key}        -> useDeleteSetting
 *   POST   /api/v1/admin/settings/{key}/reveal -> useRevealSetting
 *
 * The reveal mutation returns the cleartext value but does NOT update
 * the cached list - the list always carries the masked shape so a
 * cleartext value never leaks via React Query's persisted cache. The
 * caller is expected to display the revealed value in a transient
 * modal and discard it on close.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  SystemSettingResponse,
  SystemSettingUpsertRequest,
} from "@/types/api";

export const ADMIN_SETTINGS_QUERY_KEY = ["adminSettings"] as const;

/**
 * List system_settings rows. Secret values come back masked.
 *
 * Pass a non-null ``category`` to filter server-side. ``null`` /
 * ``undefined`` returns every row.
 */
export function useAdminSettings(
  category?: string | null,
): UseQueryResult<SystemSettingResponse[]> {
  return useQuery({
    queryKey: [...ADMIN_SETTINGS_QUERY_KEY, { category: category ?? null }],
    queryFn: async () => {
      const response = await api.get<SystemSettingResponse[]>(
        "/admin/settings",
        category ? { params: { category } } : undefined,
      );
      return response.data;
    },
  });
}

export interface UpsertSettingArgs {
  key: string;
  body: SystemSettingUpsertRequest;
}

/**
 * PUT /admin/settings/{key}. Returns the (possibly masked) row. The
 * backend masks the returned value when is_secret=true, so callers
 * cannot rely on this response to learn the cleartext value.
 */
export function useUpsertSetting(): UseMutationResult<
  SystemSettingResponse,
  Error,
  UpsertSettingArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, body }: UpsertSettingArgs) => {
      const response = await api.put<SystemSettingResponse>(
        `/admin/settings/${encodeURIComponent(key)}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ADMIN_SETTINGS_QUERY_KEY,
      });
    },
  });
}

/** DELETE /admin/settings/{key}. Returns void; 404 on missing. */
export function useDeleteSetting(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      await api.delete(`/admin/settings/${encodeURIComponent(key)}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ADMIN_SETTINGS_QUERY_KEY,
      });
    },
  });
}

/**
 * POST /admin/settings/{key}/reveal. Returns the cleartext value.
 *
 * The backend writes a SETTING_REVEALED row to system_logs every time
 * this fires, which is why we use a mutation (not a query): callers
 * MUST explicitly request the reveal.
 *
 * The returned cleartext is intentionally NOT written into the cached
 * list - see the file-level comment.
 */
export function useRevealSetting(): UseMutationResult<
  SystemSettingResponse,
  Error,
  string
> {
  return useMutation({
    mutationFn: async (key: string) => {
      const response = await api.post<SystemSettingResponse>(
        `/admin/settings/${encodeURIComponent(key)}/reveal`,
      );
      return response.data;
    },
  });
}
